import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { LogOut, WifiOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SpaDoorDisplay — corridor kiosk outside the SPA door.
 *
 *   free           → Green    "FREE"
 *   starting_soon  → Amber    "STARTING SOON · [mm:ss countdown]"
 *   in_session     → Rose     "[guest]" + big MIN LEFT + small end time
 *   changeover     → Violet   "CHANGEOVER" between two back-to-back bookings
 *
 * The backend owns the state-machine decision (`state` field on
 * /public/spa-door/status) so a client with a bad clock can never
 * mis-render. We just switch on it, tick the countdown between
 * refetches, and force an immediate refetch when the countdown hits
 * zero so the display never freezes at 00:00 (previous bug).
 *
 * Live sync: WS booking.* events trigger a silent refetch; a 15 s
 * heartbeat keeps the connection healthy; three consecutive fetch
 * failures flip the whole screen to an Offline banner asking the crew
 * to check the network.
 *
 * Also renders an optional Manager-uploaded logo whose position
 * (top-left|top-center|top-right|center|bottom-*) and size are picked
 * in Settings and shipped inline in every status response.
 */
export default function SpaDoorDisplay() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [failCount, setFailCount] = useState(0);
  const [tick, setTick] = useState(0);
  const lastLoadedAt = useRef(0);
  // Fire an immediate refetch when the local countdown crosses zero;
  // guarded with a token so we don't stampede the backend if the WS
  // event also lands in the same second.
  const zeroCrossRef = useRef({ armed: null });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/spa-door/status`, { timeout: 8000 });
      setStatus(r.data);
      setFailCount(0);
      lastLoadedAt.current = Date.now();
      zeroCrossRef.current.armed = null; // reset the zero-cross guard on every fresh state
    } catch {
      setFailCount((n) => n + 1);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Fallback refetch every 30s in case the WS drops silently.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  // Live push from any booking mutation — matches the SpaOpsView channel.
  useLiveEvents((ev) => {
    if (!ev?.type) return;
    if (ev.type.startsWith("booking.") || ev.type === "content.updated" || ev.type === "settings.updated") load();
  });
  // 1s heartbeat so the countdown ticks down smoothly without a full refetch.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const view = useMemo(() => {
    if (!status) return null;
    const now = Date.now();
    const active = status.active;
    if (status.state === "in_session" && active) {
      const end = new Date(active.end_at).getTime();
      const remainingSec = Math.max(0, Math.round((end - now) / 1000));
      const remainingMin = Math.ceil(remainingSec / 60);
      return { ...status, remainingSec, remainingMin };
    }
    if (status.state === "starting_soon" && (status.next || []).length) {
      const start = new Date(status.next[0].start_at).getTime();
      const secondsToNext = Math.max(0, Math.round((start - now) / 1000));
      return { ...status, secondsToNext };
    }
    return status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tick]);

  // When the local countdown hits 0 (either "starting_soon" reaches
  // start_at OR "in_session" reaches end_at), the backend needs to
  // reclassify — but the WS event might be seconds behind. Trigger a
  // refetch ourselves so the display transitions immediately instead
  // of freezing at 00:00 (previous bug fix).
  useEffect(() => {
    if (!view) return;
    // Zero-cross transitions: starting_soon → in_session (start_at hit),
    // in_session → free/changeover (end_at hit), AND changeover →
    // starting_soon (end_at of the primary is the next start_at when
    // we're mid-changeover). Without the changeover arm we'd stall a
    // full 30 s poll waiting for the next state.
    let key = null;
    let done = false;
    if (view.state === "starting_soon") {
      key = `ss:${view.next?.[0]?.start_at || ""}`;
      done = (view.secondsToNext ?? 1) <= 0;
    } else if (view.state === "in_session") {
      key = `is:${view.active?.end_at || ""}`;
      done = (view.remainingSec ?? 1) <= 0;
    } else if (view.state === "changeover") {
      // In changeover the "next transition" is when the follow-up
      // starts — use its start_at as the anchor.
      const nextStart = view.next?.[0]?.start_at;
      if (nextStart) {
        key = `co:${nextStart}`;
        done = new Date(nextStart).getTime() - Date.now() <= 0;
      }
    }
    if (!key) return;
    if (done && zeroCrossRef.current.armed !== key) {
      zeroCrossRef.current.armed = key;
      const t = setTimeout(load, 300);
      return () => clearTimeout(t);
    }
  }, [view, load]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  // ---- Offline handling ---------------------------------------------------
  // After 3 consecutive fetch failures OR a heartbeat gap > 90s, show a
  // full-screen offline warning. The load() call above still runs on the
  // 30s cadence in the background, so the moment the network recovers
  // the screen self-heals — no crew intervention required.
  const nowMs = Date.now();
  const staleFor = lastLoadedAt.current ? nowMs - lastLoadedAt.current : 0;
  const offline = failCount >= 3 || (status && staleFor > 90000);
  if (offline) {
    return (
      <div className="h-screen w-screen bg-slate-900 text-white flex flex-col items-center justify-center px-8 text-center" data-testid="spa-door-offline">
        <WifiOff className="h-16 w-16 mb-6 opacity-70" />
        <div className="overline text-sm tracking-[0.4em] mb-3 opacity-70">SPA · CONNECTION LOST</div>
        <div className="font-display text-[8vw] leading-tight mb-4">OFFLINE</div>
        <p className="text-lg max-w-xl opacity-80 leading-relaxed">
          The kiosk can't reach the reservation server. Please check the Wi-Fi
          or Ethernet connection — the display will resume automatically as
          soon as the network is back.
        </p>
        <button
          onClick={() => { setFailCount(0); load(); }}
          className="mt-8 px-6 py-3 border border-white/40 hover:bg-white/10 uppercase tracking-widest text-sm"
          data-testid="spa-door-retry"
        >
          Retry now
        </button>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-neutral-900 text-neutral-300" data-testid="spa-door-loading">
        <div className="font-display text-2xl tracking-widest">CONNECTING…</div>
      </div>
    );
  }

  const paletteByState = {
    free:           { bg: "bg-emerald-600",     fg: "text-white",  accent: "text-emerald-100" },
    starting_soon:  { bg: "bg-amber-500",       fg: "text-white",  accent: "text-amber-50" },
    in_session:     { bg: "bg-rose-700",        fg: "text-white",  accent: "text-rose-100" },
    changeover:     { bg: "bg-violet-700",      fg: "text-white",  accent: "text-violet-100" },
  };
  const palette = paletteByState[view.state] || paletteByState.free;

  const fmtHm = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    } catch { return ""; }
  };

  // Position map — pins the logo to the requested corner/edge using
  // absolute layout so it doesn't push the primary reading around.
  const logo = view.logo;
  const logoPositionClass = {
    "top-left":     "top-4 left-4",
    "top-center":   "top-4 left-1/2 -translate-x-1/2",
    "top-right":    "top-4 right-4",
    "center":       "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-25",
    "bottom-left":  "bottom-4 left-4",
    "bottom-center":"bottom-4 left-1/2 -translate-x-1/2",
    "bottom-right": "bottom-4 right-4",
  }[logo?.position] || "top-4 left-1/2 -translate-x-1/2";
  const logoSize = Math.max(48, Math.min(logo?.size || 120, 320));

  // Upcoming footer is now rendered in every state (previous version
  // hid it during starting_soon). Cap at 2 upcoming slots.
  const upcomingList = (view.next || []).slice(0, 2);

  return (
    <div
      className={`h-screen w-screen ${palette.bg} ${palette.fg} flex flex-col relative transition-colors duration-500 overflow-hidden`}
      data-testid={`spa-door-state-${view.state}`}
    >
      {/* Logo — Manager-configurable position + size. Absolutely
          positioned so it doesn't rearrange the main reading. */}
      {logo?.url && (
        <img
          src={logo.url}
          alt="Spa logo"
          className={`absolute ${logoPositionClass} z-10 pointer-events-none select-none`}
          style={{ height: `${logoSize}px`, width: "auto", objectFit: "contain" }}
          data-testid="spa-door-logo"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}

      <button
        onClick={doLogout}
        className="absolute top-3 right-3 opacity-30 hover:opacity-80 transition-opacity text-xs uppercase tracking-widest flex items-center gap-1 p-2 z-20"
        data-testid="spa-door-logout"
        aria-label="Sign out"
      >
        <LogOut className="h-3 w-3" />
      </button>

      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        {view.state === "free" && (
          <>
            <div className={`overline text-lg tracking-[0.4em] mb-4 opacity-80 ${palette.accent}`} data-testid="spa-door-status-label">SPA</div>
            <div className="font-display text-[18vw] leading-none tracking-tight" data-testid="spa-door-primary">FREE</div>
          </>
        )}

        {view.state === "starting_soon" && (
          <>
            <div className={`overline text-lg tracking-[0.4em] mb-4 opacity-80 ${palette.accent}`}>SPA</div>
            <div className="font-display text-[10vw] leading-tight tracking-tight" data-testid="spa-door-primary">STARTING SOON</div>
            <div className="mt-6 font-mono text-[8vw] leading-none" data-testid="spa-door-countdown">
              {String(Math.floor((view.secondsToNext || 0) / 60)).padStart(2, "0")}
              :{String((view.secondsToNext || 0) % 60).padStart(2, "0")}
            </div>
            {view.next?.[0] && (
              <div className={`mt-4 text-base tracking-wider ${palette.accent}`}>
                {view.next[0].identity} · {view.next[0].treatment_name || "Treatment"}
              </div>
            )}
          </>
        )}

        {view.state === "in_session" && view.active && (
          <>
            <div className={`overline text-lg tracking-[0.4em] mb-4 opacity-80 ${palette.accent}`}>IN SESSION</div>
            <div className="font-display text-[10vw] leading-none tracking-tight break-words max-w-full px-4" data-testid="spa-door-primary">
              {view.active.identity}
            </div>
            {view.active.treatment_name && (
              <div className={`mt-2 text-lg tracking-wider ${palette.accent}`}>
                {view.active.treatment_name}
              </div>
            )}
            <div className="mt-10 flex items-baseline gap-4">
              <div className="font-mono text-[14vw] leading-none" data-testid="spa-door-remaining-min">
                {view.remainingMin}
              </div>
              <div className={`overline text-base tracking-widest ${palette.accent}`}>MIN LEFT</div>
            </div>
            <div className={`mt-2 text-sm tracking-widest ${palette.accent}`} data-testid="spa-door-end-time">
              ends {fmtHm(view.active.end_at)}
            </div>
          </>
        )}

        {view.state === "changeover" && (
          <>
            <div className={`overline text-lg tracking-[0.4em] mb-4 opacity-80 ${palette.accent}`}>SPA</div>
            <div className="font-display text-[12vw] leading-tight tracking-tight" data-testid="spa-door-primary">CHANGEOVER</div>
            {view.next?.[0] && (
              <div className={`mt-6 text-lg tracking-wider ${palette.accent}`}>
                Next · {view.next[0].identity} at {fmtHm(view.next[0].start_at)}
              </div>
            )}
          </>
        )}
      </div>

      {/* Upcoming footer — shown in EVERY state now so guests always see
          what's on next. Hidden only when there are literally no more
          bookings today. */}
      {upcomingList.length > 0 && (
        <div className={`px-8 pb-6 pt-4 border-t border-white/20 ${palette.accent}`} data-testid="spa-door-upcoming">
          <div className="overline text-xs tracking-[0.3em] mb-2 opacity-70">TODAY'S UPCOMING</div>
          <div className="flex flex-wrap gap-x-8 gap-y-1 text-base font-mono">
            {upcomingList.map((n) => (
              <div key={n.id} className="flex items-center gap-2">
                <span className="font-bold">{fmtHm(n.start_at)}</span>
                <span className="opacity-80">·</span>
                <span className="truncate max-w-[40vw]">{n.identity}</span>
                {n.treatment_name && <span className="opacity-70 truncate max-w-[30vw]">· {n.treatment_name}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
